import { Component, Inject, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDatepicker } from '@angular/material/datepicker';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TranslocoService } from '@ngneat/transloco';
import { compareAsc, format, parseISO, startOfToday } from 'date-fns';
import { combineLatest, debounceTime, firstValueFrom, Subject, takeUntil } from 'rxjs';
import { NextServicio } from '../../services/services.types';
import { TasksService } from '../tasks.service';
import { FlushMultiTaskEditBody, MultiTaskEditChecks, RetrieveMultiTaskEditReply, TaskWorkDayTypeMode, UserAndZombieOptions, UserData, UserZombies, WorkDay, ZombieData } from '../tasks.types';
import { LocalizePipe } from '../../../../core/pipes/localize.pipe';
import { FuseConfirmationService } from '../../../../../@fuse/services/confirmation/confirmation.service';

@Component({
    selector: 'edit-multitask-dialog',
    templateUrl: './edit-multitask-dialog.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class EditMultiTaskDialogComponent implements OnInit, OnDestroy {

    loading: boolean = false;
    taskForm = new FormGroup({
        planningArea: new FormControl<string>({ value: undefined, disabled: true }, Validators.required),
        scheduled_from_date: new FormControl<Date>({ value: null, disabled: true }, Validators.required),
        scheduled_to_date: new FormControl<Date>({ value: null, disabled: true }),
        workDayTypeOid: new FormControl<string>({ value: null, disabled: true }),
        scheduled_from_time: new FormControl<string>({ value: undefined, disabled: true }),
        scheduled_to_time: new FormControl<string>({ value: undefined, disabled: true }),
        scheduled_from_time2: new FormControl<string>({ value: undefined, disabled: true }),
        scheduled_to_time2: new FormControl<string>({ value: undefined, disabled: true }),
        userId: new FormControl<string>({ value: null, disabled: true }),
        zombieNumber: new FormControl<string>({ value: null, disabled: true }),
        location: new FormControl<any>({ value: null, disabled: true })
    });
    checks: MultiTaskEditChecks = {
        planningArea: false,
        scheduled_from_date: false,
        scheduled_to_date: false,
        workDayTypeOid: false,
        scheduled_from_time: false,
        scheduled_to_time: false,
        scheduled_from_time2: false,
        scheduled_to_time2: false,
        userId: false,
        zombieNumber: false,
        location: false
    };

    visibleUser: boolean = true;
    visibleZombie: boolean = true;
    visibleLocation: boolean = true;

    addressOptions: any[] = [];

    today: string;

    userZombiesAssignments: UserZombies;
    selectedUser: UserData;
    selectedZombie: ZombieData;

    isTaskEndsNextDay: boolean = false;
    isTaskMoreThan8h: boolean = false;

    changeUserZombieLink: boolean = false;

    private originalMultiTaskEditData: RetrieveMultiTaskEditReply;

    private unsubscribeAll: Subject<void> = new Subject();

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: {
            service: NextServicio;
            multiTaskEditData: RetrieveMultiTaskEditReply;
            planningAreaId: string;
        },
        private tasksService: TasksService,
        public dialog: MatDialogRef<EditMultiTaskDialogComponent>,
        private transloco: TranslocoService,
        public localize: LocalizePipe,
        private fuseConfirmationService: FuseConfirmationService,
    ) { }

    async ngOnInit() {
        this.originalMultiTaskEditData = structuredClone(this.data.multiTaskEditData);
        this.today = format(startOfToday(), 'yyyy-MM-dd');
        this.populateLocalVariables(this.data.multiTaskEditData);

        this.taskForm.get('planningArea').valueChanges
            .pipe(takeUntil(this.unsubscribeAll))
            .subscribe(async (planningAreaId) => {
                if (planningAreaId) {
                    this.userZombiesAssignments = await firstValueFrom(this.tasksService.getTaskAssignments(this.data.service.serviceId, planningAreaId,
                        this.taskForm.get('scheduled_from_date').value ? format(this.taskForm.get('scheduled_from_date').value, 'yyyy-MM-dd') : this.today));
                    const userOptions: UserAndZombieOptions[] = this.userZombiesAssignments.users.map(user =>
                        ({ code: user.userId, value: { [this.transloco.getActiveLang()]: user.fullname }, isDefault: false, _deprecated: false }));
                    this.data.multiTaskEditData.user = { diffNumber: 1, options: userOptions, userId: undefined, name: undefined };
                    this.visibleUser = true;
                    this.taskForm.get('userId').setValue(undefined);
                    this.changeChecks(true, 'userId');

                    const zombieOptions: UserAndZombieOptions[] = this.userZombiesAssignments.zombies.map(zombie =>
                        ({ code: String(zombie.zombieNumber), value: { [this.transloco.getActiveLang()]: zombie.name }, isDefault: false, _deprecated: false }));
                    this.data.multiTaskEditData.zombie = { diffNumber: 1, options: zombieOptions, zombieName: undefined, zombieNumber: undefined };
                    this.visibleZombie = true;
                    this.taskForm.get('zombieNumber').setValue('0');
                    this.changeChecks(true, 'zombieNumber');
                }
            });

        this.taskForm.get('location').valueChanges.pipe(
            takeUntil(this.unsubscribeAll),
            debounceTime(500)
        ).subscribe((address) => {
            if (address && typeof address === 'string') {
                this.tasksService.searchAddress(address, this.data.multiTaskEditData.address?.addressUri).pipe(takeUntil(this.unsubscribeAll)).subscribe((reply) => {
                    this.addressOptions = reply.data;
                });
            } else if (!address) {
                this.tasksService.searchAddress('', this.data.multiTaskEditData.address?.addressUri).pipe(takeUntil(this.unsubscribeAll)).subscribe((reply) => {
                    this.addressOptions = reply.data;
                });
            }
        });

        this.taskForm.get('zombieNumber').valueChanges.pipe(takeUntil(this.unsubscribeAll)).subscribe((value) => {
            this.selectedZombie = this.userZombiesAssignments?.zombies.find(z => z.zombieNumber === Number(value));
            if (value) {
                if (!this.taskForm.get('userId').value) {
                    if (!(this.data.multiTaskEditData?.user?.diffNumber > 1 && this.data.multiTaskEditData?.zombie?.diffNumber === 1)) {
                        if (this.userZombiesAssignments?.zombies.find(z => z.zombieNumber === Number(value))) {
                            const opt = this.data.multiTaskEditData.user.options.find(u =>
                                u.code === this.userZombiesAssignments?.zombies.find(z => z.zombieNumber === Number(value)).userId);
                            this.taskForm.get('userId').setValue(opt.code);
                            this.changeChecks(true, 'userId');
                        }
                    }
                }
            }
        });

        this.taskForm.get('userId').valueChanges.pipe(takeUntil(this.unsubscribeAll)).subscribe((value) => {
            this.selectedUser = this.userZombiesAssignments?.users.find(u => u.userId === value);
            this.changeUserZombieCheck(false);
            if (value) {
                if (this.taskForm.get('zombieNumber').value === '0') {
                    const opt = this.data.multiTaskEditData.zombie.options.find(z =>
                        Number(z.code) === this.userZombiesAssignments?.users.find(u => u.userId === value)?.zombieNumber);
                    this.taskForm.get('zombieNumber').setValue(opt.code);
                }
            }
        });

        this.taskForm.get('workDayTypeOid').valueChanges
            .pipe(takeUntil(this.unsubscribeAll))
            .subscribe((value) => {
                const workday = this.data.multiTaskEditData.workDayType.options.find(opt => opt.code === value);
                const exportObject: WorkDay = workday?.export;
                if (exportObject?.mode === TaskWorkDayTypeMode.split) {
                    if (!this.data.multiTaskEditData.scheduled_from_time2) {
                        this.data.multiTaskEditData.scheduled_from_time2 = { diffNumber: 1, scheduled_from_time2: exportObject.from2 };
                    }
                    if (!this.data.multiTaskEditData.scheduled_to_time2) {
                        this.data.multiTaskEditData.scheduled_to_time2 = { diffNumber: 1, scheduled_to_time2: exportObject.to2 };
                    }
                } else {
                    this.data.multiTaskEditData.scheduled_from_time2 = undefined;
                    this.data.multiTaskEditData.scheduled_to_time2 = undefined;
                }
                if (!this.data.multiTaskEditData.scheduled_from_time && this.checks['workDayTypeOid']) {
                    this.data.multiTaskEditData.scheduled_from_time = { diffNumber: 1, scheduled_from_time: exportObject?.from };
                }
                if (!this.data.multiTaskEditData.scheduled_to_time && this.checks['workDayTypeOid']) {
                    this.data.multiTaskEditData.scheduled_to_time = { diffNumber: 1, scheduled_to_time: exportObject?.to };
                }

                if (exportObject?.from) {
                    this.taskForm.get('scheduled_from_time').setValue(exportObject.from);
                    this.changeChecks(true, 'scheduled_from_time');
                }
                if (exportObject?.from2) {
                    this.taskForm.get('scheduled_from_time2').setValue(exportObject.from2);
                    this.changeChecks(true, 'scheduled_from_time2');
                } else {
                    this.taskForm.get('scheduled_from_time2').setValue(undefined);
                    this.changeChecks(false, 'scheduled_from_time2');
                }
                if (exportObject?.to) {
                    this.taskForm.get('scheduled_to_time').setValue(exportObject.to);
                    this.changeChecks(true, 'scheduled_to_time');
                }
                if (exportObject?.to2) {
                    this.taskForm.get('scheduled_to_time2').setValue(exportObject.to2);
                    this.changeChecks(true, 'scheduled_to_time2');
                } else {
                    this.taskForm.get('scheduled_to_time2').setValue(undefined);
                    this.changeChecks(false, 'scheduled_to_time2');
                }
            });

        if (!this.data.multiTaskEditData.scheduled_from_time2 && !this.data.multiTaskEditData.scheduled_to_time2) {
            combineLatest([this.taskForm.get('scheduled_from_time').valueChanges, this.taskForm.get('scheduled_to_time').valueChanges])
                .pipe(takeUntil(this.unsubscribeAll)).subscribe(([valueFrom, valueTo]) => {
                    if (valueFrom && valueTo && valueFrom > valueTo) {
                        this.isTaskEndsNextDay = true;
                    } else {
                        this.isTaskEndsNextDay = false;
                    }
                    if (valueFrom && valueTo) {
                        if (valueFrom <= valueTo) {
                            if (this.getDiffHours(valueTo, valueFrom) > 8) {
                                this.isTaskMoreThan8h = true;
                            } else {
                                this.isTaskMoreThan8h = false;
                            }
                        } else {
                            if (24 - this.getDiffHours(valueFrom, valueTo) > 8) {
                                this.isTaskMoreThan8h = true;
                            } else {
                                this.isTaskMoreThan8h = false;
                            }
                        }
                    } else {
                        this.isTaskMoreThan8h = false;
                    }
                });
        } else {
            combineLatest([this.taskForm.get('scheduled_from_time').valueChanges, this.taskForm.get('scheduled_from_time2').valueChanges,
            this.taskForm.get('scheduled_to_time').valueChanges, this.taskForm.get('scheduled_to_time2').valueChanges])
                .pipe(takeUntil(this.unsubscribeAll)).subscribe(([valueFrom, valueFrom2, valueTo, valueTo2]) => {
                    if (valueFrom && valueTo2 && valueFrom > valueTo2) {
                        this.isTaskEndsNextDay = true;
                    } else {
                        this.isTaskEndsNextDay = false;
                    }
                    if (valueFrom2 && valueTo2) {
                        if (valueTo < valueFrom && valueFrom2 < valueTo2) {
                            this.isTaskMoreThan8h = (24 - this.getDiffHours(valueFrom, valueTo)) + this.getDiffHours(valueTo2, valueFrom2) > 8;
                        } else if (valueTo2 < valueFrom2 && valueFrom < valueTo) {
                            this.isTaskMoreThan8h = (24 - this.getDiffHours(valueFrom2, valueTo2)) + this.getDiffHours(valueTo, valueFrom) > 8;
                        } else if (valueTo2 < valueFrom2 && valueTo < valueFrom) {
                            this.isTaskMoreThan8h = (24 - this.getDiffHours(valueFrom2, valueTo2)) + (24 - this.getDiffHours(valueFrom, valueTo)) > 8;
                        } else {
                            this.isTaskMoreThan8h = this.getDiffHours(valueTo2, valueFrom2) + this.getDiffHours(valueTo, valueFrom) > 8;
                        }
                    } else {
                        if (valueTo < valueFrom) {
                            this.isTaskMoreThan8h = (24 - this.getDiffHours(valueFrom, valueTo)) > 8;
                        } else {
                            this.isTaskMoreThan8h = this.getDiffHours(valueTo, valueFrom) > 8;
                        }
                    }
                });
        }
    }

    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this.unsubscribeAll.next();
        this.unsubscribeAll.complete();
    }

    openDatepickerOnClick(datepicker: MatDatepicker<Date>) {
        if (!datepicker.opened) {
            datepicker.open();
        }
    }

    changeChecks(check: boolean, key: string) {
        this.checks[key] = check;
        if (check) {
            this.taskForm.get([key]).enable();
        } else {
            if (key === 'workDayTypeOid') {
                this.data.multiTaskEditData.scheduled_from_time = this.originalMultiTaskEditData.scheduled_from_time;
                this.data.multiTaskEditData.scheduled_from_time2 = this.originalMultiTaskEditData.scheduled_from_time2;
                this.data.multiTaskEditData.scheduled_to_time = this.originalMultiTaskEditData.scheduled_to_time;
                this.data.multiTaskEditData.scheduled_to_time2 = this.originalMultiTaskEditData.scheduled_to_time2;
                this.taskForm.get('workDayTypeOid').setValue(this.data.multiTaskEditData.workDayType?.workDayTypeOid);
            }
            if (key === 'planningArea') {
                this.data.multiTaskEditData.user = this.originalMultiTaskEditData.user;
                this.data.multiTaskEditData.zombie = this.originalMultiTaskEditData.zombie;
                this.taskForm.get('planningArea').setValue(this.data.multiTaskEditData.planningArea.planningAreaOid);
            }
            this.taskForm.get([key]).disable();
        }
    }

    clearAddress() {
        this.taskForm.get('location').setValue(undefined);
    }

    changeUserZombieCheck(check: boolean) {
        this.changeUserZombieLink = check;
    }

    timeIsValid() {
        let valid: boolean = true;
        if (this.checks.scheduled_from_time && !this.taskForm.get('scheduled_from_time').valid) {
            valid = false;
            return valid;
        }
        if (this.checks.scheduled_from_time2 && !this.taskForm.get('scheduled_from_time2').valid) {
            valid = false;
            return valid;
        }
        if (this.checks.scheduled_to_time && !this.taskForm.get('scheduled_to_time').valid) {
            valid = false;
            return valid;
        }
        if (this.checks.scheduled_to_time2 && !this.taskForm.get('scheduled_to_time2').valid) {
            valid = false;
            return valid;
        }
        return valid;
    }

    datesIsValid() {
        let valid: boolean = true;
        if(!this.taskForm.get('scheduled_from_date').valid && this.checks.scheduled_from_date) {
            return valid = false;
        }
        if(!this.taskForm.get('scheduled_to_date').valid && this.checks.scheduled_to_date) {
            return valid = false;
        }
        return valid;
    }

    myDateFilter = (d: Date | null): boolean => {
        const day = (d || new Date());
        const today = new Date();
        const virtualScheduledDay = new Date(this.data.multiTaskEditData.scheduled_from_date.scheduled_from_date);

        day.setHours(2);
        virtualScheduledDay.setHours(2);

        today.setHours(2);
        today.setMinutes(0);
        today.setSeconds(0);
        today.setMilliseconds(0);

        return compareAsc(virtualScheduledDay, day) === 0 || compareAsc(today, day) === 0 || day > today;
    };

    updateTasks() {
        this.taskForm.markAllAsTouched();
        if ((this.datesIsValid()) && (this.timeIsValid())) {
            const confirmDialog = this.openConfirmDialog(this.transloco.translate('services.dialog.multiTaskEdit.confirmUpdate'));
            confirmDialog.afterClosed().subscribe(async (confirm) => {
                if (confirm === 'confirmed') {
                    const updateFieldName: string[] = [];
                    for (const key of Object.keys(this.checks)) {
                        switch (key) {
                            case 'location':
                                if (this.checks[key]) {
                                    updateFieldName.push('addressOid');
                                }
                                break;
                            default:
                                if (this.checks[key]) {
                                    updateFieldName.push(key);
                                }
                        }
                    }
                    const body: FlushMultiTaskEditBody = {
                        serviceId: this.data.service.serviceId,
                        taskId: this.data.multiTaskEditData.effectiveTaskId,
                        planningArea: this.checks.planningArea ? this.taskForm.get('planningArea').value : undefined,
                        scheduled_from_date: this.taskForm.get('scheduled_from_date').value ? format(this.taskForm.get('scheduled_from_date').value, 'yyyy-MM-dd') : undefined,
                        scheduled_to_date: this.taskForm.get('scheduled_to_date').value ? format(this.taskForm.get('scheduled_to_date').value, 'yyyy-MM-dd') : undefined,
                        scheduled_from_time: this.taskForm.get('scheduled_from_time').value,
                        scheduled_to_time: this.taskForm.get('scheduled_to_time').value,
                        scheduled_from_time2: this.taskForm.get('scheduled_from_time2').value,
                        scheduled_to_time2: this.taskForm.get('scheduled_to_time2').value,
                        userId: this.checks.userId ? this.taskForm.get('userId').value : undefined,
                        addressOid: this.taskForm.get('location').value?._oid,
                        addressUri: this.data.multiTaskEditData.address?.addressUri,
                        workDayTypeOid: this.taskForm.get('workDayTypeOid').value,
                        workDayTypeUri: this.data.multiTaskEditData.workDayType?.workDayTypeUri,
                        changeUserZombieLink: this.changeUserZombieLink,
                        updateFieldName: updateFieldName
                    };
                    if (!this.data.service.generateUsernamesManually) {
                        body.zombieNumber = Number(this.taskForm.get('zombieNumber').value);
                    }

                    this.tasksService.flushMultiTaskEdit(body).pipe(takeUntil(this.unsubscribeAll)).subscribe(async (reply) => {
                        if (reply.status === 200) {
                            this.openWarningDialog(
                                this.transloco.translate('services.dialog.multiTaskEdit.hasBeenUpdatedSomeTasks', { tasks: reply.effectiveTaskId.length }));
                            this.closeDialog(true);
                        } else {
                            this.openWarningDialog(this.transloco.translate('services.dialog.multiTaskEdit.errorFlushingMessage'));
                        }
                    });
                }
            });
        } else {
            this.openWarningDialog(this.transloco.translate('services.dialog.multiTaskEdit.invalidForm'));
        }
    }

    closeDialog(update?: boolean) {
        this.dialog.close(update);
    }

    displayAddress(address: any) {
        return address?.name ?? '';
    }

    displayUserAndZombie(option: any) {
        if (option) {
            return option.value ? Object.values(option.value) : null;
        }
    }

    private getDiffHours(hhmmssFrom: string, hhmmssTo: string): number {
        const dataFrom = hhmmssFrom.split(':');
        const dataTo = hhmmssTo.split(':');
        return Number(dataFrom[0]) + Number(dataFrom[1]) / 60 - Number(dataTo[0]) - Number(dataTo[1]) / 60;
    }

    private async populateLocalVariables(data: RetrieveMultiTaskEditReply) {
        if (data.planningArea?.diffNumber === 1) {
            this.taskForm.get('planningArea').setValue(data.planningArea.planningAreaOid, { emitEvent: false });
            this.changeChecks(true, 'planningArea');
        }
        if (data.scheduled_from_time2 && data.scheduled_to_time2) {
            this.changeChecks(true, 'scheduled_from_time2');
            this.changeChecks(true, 'scheduled_to_time2');
            this.changeChecks(true, 'scheduled_from_time');
            this.changeChecks(true, 'scheduled_to_time');
            this.taskForm.get('scheduled_from_time2').addValidators(Validators.required);
            this.taskForm.get('scheduled_to_time2').addValidators(Validators.required);
            this.taskForm.get('scheduled_from_time').addValidators(Validators.required);
            this.taskForm.get('scheduled_to_time').addValidators(Validators.required);
            this.taskForm.get('scheduled_from_time2').updateValueAndValidity();
            this.taskForm.get('scheduled_to_time2').updateValueAndValidity();
            this.taskForm.get('scheduled_from_time').updateValueAndValidity();
            this.taskForm.get('scheduled_to_time').updateValueAndValidity();
            if (data.scheduled_from_time2.diffNumber === 1) {
                this.taskForm.get('scheduled_from_time2').setValue(data.scheduled_from_time2?.scheduled_from_time2);
            }
            if (data.scheduled_to_time2.diffNumber === 1) {
                this.taskForm.get('scheduled_to_time2').setValue(data.scheduled_to_time2.scheduled_to_time2);
            }
        } else {
            this.taskForm.get('scheduled_from_time2').clearValidators();
            this.taskForm.get('scheduled_to_time2').clearValidators();
            this.taskForm.get('scheduled_from_time').clearValidators();
            this.taskForm.get('scheduled_to_time').clearValidators();

            this.taskForm.get('scheduled_from_time2').updateValueAndValidity();
            this.taskForm.get('scheduled_to_time2').updateValueAndValidity();
            this.taskForm.get('scheduled_from_time').updateValueAndValidity();
            this.taskForm.get('scheduled_to_time').updateValueAndValidity();
        }

        if (this.data.planningAreaId) {
            this.userZombiesAssignments = await firstValueFrom(this.tasksService.getTaskAssignments(this.data.service.serviceId, this.data.planningAreaId, data.scheduled_from_date?.scheduled_from_date ?? format(startOfToday(), 'yyyy-MM-dd')));
        }
        if (data.scheduled_from_date?.diffNumber === 1) {
            this.taskForm.get('scheduled_from_date').setValue(data.scheduled_from_date?.scheduled_from_date ? parseISO(data.scheduled_from_date.scheduled_from_date) : undefined);
            this.changeChecks(true, 'scheduled_from_date');
        }
        if (data.scheduled_to_date) {
            this.taskForm.get('scheduled_to_date').addValidators(Validators.required);
            this.taskForm.get('scheduled_to_date').updateValueAndValidity();
        }
        if (data.scheduled_to_date?.diffNumber === 1) {
            this.taskForm.get('scheduled_to_date').setValue(data.scheduled_to_date?.scheduled_to_date ? parseISO(data.scheduled_to_date?.scheduled_to_date) : undefined);
            this.changeChecks(true, 'scheduled_to_date');
        }
        if (data.workDayType?.diffNumber === 1) {
            this.taskForm.get('workDayTypeOid').setValue(data.workDayType?.workDayTypeOid);
            if (data.workDayType?.workDayTypeOid) {
                this.changeChecks(true, 'workDayTypeOid');
            }
        }
        if (data?.scheduled_from_time?.diffNumber === 1) {
            this.taskForm.get('scheduled_from_time').setValue(data.scheduled_from_time?.scheduled_from_time);
            if (data.scheduled_from_time?.scheduled_from_time) {
                this.changeChecks(true, 'scheduled_from_time');
            }
        }
        if (data?.scheduled_to_time?.diffNumber === 1) {
            this.taskForm.get('scheduled_to_time').setValue(data.scheduled_to_time.scheduled_to_time);
            if (data.scheduled_to_time?.scheduled_to_time) {
                this.changeChecks(true, 'scheduled_to_time');
            }
        }
        if (data.user) {
            if (data.user.diffNumber === 1) {
                if (!data.user.userId) {
                    data.user.userId = null;
                }
                if (data.user.options) {
                    const value = data.user.options.find(u => u.code === data.user.userId);
                    this.taskForm.get('userId').setValue(value?.code);
                } else {
                    this.taskForm.get('userId').setValue(undefined);
                }
                this.changeChecks(true, 'userId');
            }
        } else {
            this.visibleUser = false;
        }

        if (data.zombie) {
            if (data.zombie.diffNumber === 1) {
                if (data.zombie.options) {
                    const value = data.zombie.options.find(u => Number(u.code) === data.zombie.zombieNumber);
                    this.taskForm.get('zombieNumber').setValue(value?.code);
                } else {
                    this.taskForm.get('zombieNumber').setValue('0');
                }
                this.changeChecks(true, 'zombieNumber');
            } else {
                this.taskForm.get('zombieNumber').setValue('0');
            }
        } else {
            this.visibleZombie = false;
        }

        if (data.address && data.address.diffNumber === 1) {
            this.taskForm.get('location').setValue(
                { name: data.address.addressName, _oid: data.address?.addressOid, uri: data.address?.addressUri });
            if (data.address?.addressOid) {
                this.changeChecks(true, 'location');
            }
        } else if (data.address && data.address.diffNumber > 1) {
            this.taskForm.get('location').setValue(undefined);
        } else {
            this.visibleLocation = false;
        }
    }

    private openConfirmDialog(message: string) {
        return this.fuseConfirmationService.open({
            title: this.transloco.translate('warning'),
            message: message,
            actions: {
                confirm: {
                    label: this.transloco.translate('confirm')
                },
                cancel: {
                    label: this.transloco.translate('cancel')
                }
            }
        });
    }

    private openWarningDialog(message: string) {
        return this.fuseConfirmationService.open({
            title: this.transloco.translate('warning'),
            message: message,
            actions: {
                confirm: {
                    label: this.transloco.translate('confirm')
                },
                cancel: {
                    label: this.transloco.translate('cancel'),
                    show: false
                }
            }
        });
    }

}
